import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Package, ShoppingBag, Users, Tag, Gavel, MessageSquare,
  Search, Plus, Trash2, Edit2, X, Send, ExternalLink, ChevronRight
} from "lucide-react";
import { api, formatPrice, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const BLANK = {
  sku: "", name: "", description: "", price: 0, currency: "INR",
  images: [""], category: "Rings", metal: "18K Gold", color: "Yellow Gold",
  gemstone: "", occasion: "Wedding", purity: "", weight: "", hallmark: "BIS Hallmarked",
  stock: 0, tags: [], is_new: false, is_best_seller: false,
  is_personalized: false, is_lab_grown: false, is_birthstone: false,
};

const NAV = [
  ["dashboard", "Dashboard", LayoutDashboard],
  ["products", "Products", Package],
  ["orders", "Orders", ShoppingBag],
  ["customers", "Customers", Users],
  ["coupons", "Coupons", Tag],
  ["auctions", "Auctions", Gavel],
];

export default function Admin() {
  const { user, loading, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("dashboard");

  useEffect(() => {
    if (!loading && (!user || user.role !== "admin")) nav("/login");
  }, [user, loading, nav]);

  if (!user || user.role !== "admin") return null;

  return (
    <div className="grid md:grid-cols-[260px_1fr] min-h-[calc(100vh-80px)] -mt-20 pt-20" data-testid="admin-page">
      {/* Sidebar */}
      <aside className="border-r p-6 md:p-8" style={{ borderColor: "var(--ja-border)", background: "var(--ja-ivory)" }}>
        <div className="mb-10">
          <p className="ja-overline">Atelier Console</p>
          <p className="ja-serif text-2xl mt-2">Jewels Ashu</p>
          <p className="text-xs mt-1" style={{ color: "var(--ja-muted)" }}>{user.email}</p>
        </div>
        <nav className="flex md:flex-col gap-1 overflow-x-auto md:overflow-visible">
          {NAV.map(([k, label, Icon]) => (
            <button
              key={k}
              onClick={() => setTab(k)}
              className={`flex items-center gap-3 px-4 py-3 text-xs tracking-[0.2em] uppercase transition-all ${tab === k ? "text-black" : ""}`}
              style={{ background: tab === k ? "white" : "transparent", color: tab === k ? "var(--ja-black)" : "var(--ja-muted)" }}
              data-testid={`admin-tab-${k}`}
            >
              <Icon size={15} /> {label}
              {tab === k && <ChevronRight size={14} className="ml-auto" />}
            </button>
          ))}
        </nav>
        <div className="mt-10 pt-6 border-t space-y-2" style={{ borderColor: "var(--ja-border)" }}>
          <Link to="/" className="block text-xs tracking-[0.2em] uppercase py-2">← Storefront</Link>
          <button onClick={() => { logout(); nav("/"); }} className="block text-xs tracking-[0.2em] uppercase py-2 text-left" data-testid="admin-logout">Sign Out</button>
        </div>
      </aside>

      {/* Main */}
      <main className="p-6 md:p-12 max-w-full overflow-x-hidden">
        {tab === "dashboard" && <DashboardTab />}
        {tab === "products" && <ProductsTab />}
        {tab === "orders" && <OrdersTab />}
        {tab === "customers" && <CustomersTab />}
        {tab === "coupons" && <CouponsTab />}
        {tab === "auctions" && <AuctionsTab />}
      </main>
    </div>
  );
}

/* ---------- Dashboard ---------- */
function DashboardTab() {
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data));
    api.get("/orders/all").then((r) => setRecent(r.data.slice(0, 5)));
  }, []);
  if (!stats) return <p>Loading…</p>;
  return (
    <div>
      <SectionHead title="Dashboard" sub="Live overview of your atelier." />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-6" data-testid="admin-stats">
        {[
          ["Products", stats.products],
          ["Orders", stats.orders],
          ["Customers", stats.customers],
          ["Revenue", formatPrice(stats.revenue)],
        ].map(([k, v]) => (
          <div key={k} className="p-8 border" style={{ borderColor: "var(--ja-border)", background: "white" }}>
            <p className="ja-overline" style={{ color: "var(--ja-muted)" }}>{k}</p>
            <p className="ja-serif text-4xl mt-3">{v}</p>
          </div>
        ))}
      </div>
      <div className="mt-12">
        <p className="ja-overline mb-4">Recent Orders</p>
        {recent.length === 0 ? <p className="text-sm py-8" style={{ color: "var(--ja-muted)" }}>No orders yet.</p> : (
          <div className="border" style={{ borderColor: "var(--ja-border)", background: "white" }}>
            {recent.map((o) => (
              <div key={o.id} className="grid grid-cols-4 gap-4 px-6 py-4 border-b text-sm" style={{ borderColor: "var(--ja-border)" }}>
                <span className="ja-serif">{o.order_number}</span>
                <span>{o.items.length} item{o.items.length>1?"s":""}</span>
                <span>{formatPrice(o.total)}</span>
                <span className="text-xs tracking-[0.2em] uppercase" style={{ color: "var(--ja-gold)" }}>{o.status}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------- Products with SKU search ---------- */
function ProductsTab() {
  const [products, setProducts] = useState([]);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(BLANK);

  const load = async () => {
    const { data } = await api.get("/products", { params: { limit: 200 } });
    setProducts(data);
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!q) return products;
    const s = q.toLowerCase();
    return products.filter((p) =>
      p.sku.toLowerCase().includes(s) ||
      p.name.toLowerCase().includes(s) ||
      (p.category || "").toLowerCase().includes(s)
    );
  }, [q, products]);

  const open = (p) => { setEditing(p?.id || "new"); setForm(p ? { ...p, images: p.images?.length ? p.images : [""], tags: p.tags || [] } : { ...BLANK }); };
  const close = () => { setEditing(null); setForm(BLANK); };

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        price: parseFloat(form.price) || 0,
        compare_at_price: form.compare_at_price ? parseFloat(form.compare_at_price) : null,
        stock: parseInt(form.stock) || 0,
        images: form.images.filter(Boolean),
      };
      if (editing === "new") await api.post("/products", payload);
      else await api.put(`/products/${editing}`, payload);
      toast.success("Saved"); close(); load();
    } catch (e2) { toast.error(formatApiError(e2.response?.data?.detail) || "Save failed"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await api.delete(`/products/${id}`); toast.success("Deleted"); load();
  };

  const stockTone = (s) => s === 0 ? "var(--ja-error)" : s < 5 ? "var(--ja-gold)" : "var(--ja-black)";

  return (
    <div>
      <SectionHead title="Products" sub={`${products.length} pieces in the atelier.`}>
        <button onClick={() => open(null)} className="ja-btn-outline flex items-center gap-2" data-testid="admin-new-product"><Plus size={14} /> New</button>
      </SectionHead>

      {/* SKU search */}
      <div className="flex items-center gap-3 border p-3 mb-6" style={{ borderColor: "var(--ja-border)", background: "white" }}>
        <Search size={16} />
        <input
          value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Search by SKU, name or category — e.g. JA-R001"
          className="flex-1 bg-transparent text-sm focus:outline-none"
          data-testid="sku-search"
        />
        {q && <button onClick={() => setQ("")} className="text-xs tracking-[0.2em] uppercase">Clear</button>}
      </div>

      <div className="overflow-x-auto border" style={{ borderColor: "var(--ja-border)", background: "white" }}>
        <table className="w-full text-sm">
          <thead><tr className="border-b text-left text-[11px] tracking-[0.2em] uppercase" style={{ borderColor: "var(--ja-border)" }}>
            <th className="py-4 pl-6">Image</th><th>SKU</th><th>Name</th><th>Category</th><th>Price</th><th>Stock Status</th><th></th>
          </tr></thead>
          <tbody>
            {filtered.map((p) => (
              <tr key={p.id} className="border-b" style={{ borderColor: "var(--ja-border)" }} data-testid={`admin-product-${p.sku}`}>
                <td className="py-3 pl-6"><img src={p.images?.[0]} alt="" className="w-14 h-14 object-cover" /></td>
                <td className="font-mono text-xs">{p.sku}</td>
                <td className="ja-serif">{p.name}</td>
                <td>{p.category}</td>
                <td>{formatPrice(p.price, p.currency)}</td>
                <td style={{ color: stockTone(p.stock) }}>
                  <span className="text-xs tracking-[0.2em] uppercase">
                    {p.stock === 0 ? "Sold Out" : p.stock < 5 ? `Low · ${p.stock} left` : `In Stock · ${p.stock}`}
                  </span>
                </td>
                <td className="flex gap-3 py-3 pr-6">
                  <button onClick={() => open(p)} data-testid={`admin-edit-${p.sku}`}><Edit2 size={14} /></button>
                  <button onClick={() => del(p.id)} data-testid={`admin-delete-${p.sku}`}><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={7} className="py-10 text-center" style={{ color: "var(--ja-muted)" }}>No products match "{q}".</td></tr>}
          </tbody>
        </table>
      </div>

      {editing && <ProductDialog form={form} setForm={setForm} editing={editing} onClose={close} onSave={save} />}
    </div>
  );
}

/* ---------- Orders with detail drawer + messaging ---------- */
function OrdersTab() {
  const [orders, setOrders] = useState([]);
  const [selected, setSelected] = useState(null);

  const load = async () => { const { data } = await api.get("/orders/all"); setOrders(data); };
  useEffect(() => { load(); }, []);

  return (
    <div>
      <SectionHead title="Orders" sub={`${orders.length} order${orders.length === 1 ? "" : "s"} received.`} />
      <div className="overflow-x-auto border" style={{ borderColor: "var(--ja-border)", background: "white" }}>
        <table className="w-full text-sm">
          <thead><tr className="border-b text-left text-[11px] tracking-[0.2em] uppercase" style={{ borderColor: "var(--ja-border)" }}>
            <th className="py-4 pl-6">Order #</th><th>Date</th><th>Items</th><th>Total</th><th>Payment</th><th>Status</th><th></th>
          </tr></thead>
          <tbody>
            {orders.map((o) => (
              <tr key={o.id} className="border-b cursor-pointer hover:bg-[var(--ja-cream)]" style={{ borderColor: "var(--ja-border)" }} onClick={() => setSelected(o.id)} data-testid={`admin-order-${o.order_number}`}>
                <td className="py-4 pl-6 ja-serif">{o.order_number}</td>
                <td>{new Date(o.created_at).toLocaleDateString()}</td>
                <td>{o.items.length}</td>
                <td>{formatPrice(o.total, o.currency)}</td>
                <td className="text-xs tracking-[0.2em] uppercase">{o.payment_method}</td>
                <td><StatusPill status={o.status} /></td>
                <td className="pr-6 text-xs tracking-[0.2em] uppercase" style={{ color: "var(--ja-gold)" }}>View →</td>
              </tr>
            ))}
            {orders.length === 0 && <tr><td colSpan={7} className="py-10 text-center" style={{ color: "var(--ja-muted)" }}>No orders yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {selected && <OrderDrawer orderId={selected} onClose={() => setSelected(null)} onUpdated={load} />}
    </div>
  );
}

const STATUSES = ["Order Placed", "Packed", "Shipped", "Out For Delivery", "Delivered", "Cancelled"];

function OrderDrawer({ orderId, onClose, onUpdated }) {
  const [d, setD] = useState(null);
  const [tracking, setTracking] = useState({ status: "", tracking_id: "", tracking_url: "", courier: "" });
  const [msg, setMsg] = useState({ subject: "", body: "" });
 const load = useCallback(async () => {
  const { data } = await api.get(`/admin/orders/${orderId}`);
  setD(data);
  setTracking({
    status: data.order.status,
    tracking_id: data.order.tracking_id || "",
    tracking_url: data.order.tracking_url || "",
    courier: data.order.courier || "",
  });
}, [orderId]);
  useEffect(() => {
  load();
}, [load]);
  const saveStatus = async (e) => {
    e.preventDefault();
    await api.put(`/admin/orders/${orderId}/status`, tracking);
    toast.success("Order updated"); onUpdated(); load();
  };

  const sendMsg = async (e) => {
    e.preventDefault();
    if (!msg.body) return;
    await api.post("/admin/messages", {
      to_user_id: d.customer.id, subject: msg.subject || `Update on ${d.order.order_number}`,
      body: msg.body, order_id: d.order.id,
    });
    toast.success("Message sent to buyer");
    setMsg({ subject: "", body: "" });
    load();
  };

  if (!d) return null;
  const { order, customer, messages } = d;

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="order-drawer">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative w-full max-w-2xl bg-white h-full overflow-y-auto">
        <div className="sticky top-0 bg-white border-b px-8 py-5 flex justify-between items-center z-10" style={{ borderColor: "var(--ja-border)" }}>
          <div>
            <p className="ja-overline">Order Detail</p>
            <p className="ja-serif text-2xl mt-1">{order.order_number}</p>
          </div>
          <div className="flex items-center gap-3">
            <Link to={`/order/${order.id}/invoice`} target="_blank" className="text-xs tracking-[0.2em] uppercase flex items-center gap-1" data-testid="drawer-invoice"><ExternalLink size={12} /> Invoice</Link>
            <button onClick={onClose} data-testid="drawer-close"><X size={18} /></button>
          </div>
        </div>

        <div className="p-8 space-y-10">
          {/* Buyer */}
          <section>
            <p className="ja-overline mb-4">Buyer</p>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <Field k="Name" v={customer?.name} />
              <Field k="Email" v={customer?.email} />
              <Field k="Phone" v={order.address.phone} />
              <Field k="Country" v={order.address.country} />
              <div className="col-span-2">
                <p className="ja-overline" style={{ color: "var(--ja-muted)" }}>Shipping Address</p>
                <p className="mt-2 text-sm">
                  {order.address.full_name}, {order.address.line1}
                  {order.address.line2 ? `, ${order.address.line2}` : ""}, {order.address.city}, {order.address.state} {order.address.pincode}
                </p>
              </div>
            </div>
          </section>

          {/* Items */}
          <section>
            <p className="ja-overline mb-4">Items</p>
            <div className="space-y-3">
              {order.items.map((i, idx) => (
                <div key={idx} className="flex gap-4 items-center border p-3" style={{ borderColor: "var(--ja-border)" }}>
                  <img src={i.image} alt="" className="w-14 h-14 object-cover" />
                  <div className="flex-1">
                    <p className="ja-serif">{i.name}</p>
                    <p className="text-xs" style={{ color: "var(--ja-muted)" }}>SKU {i.sku} · × {i.quantity}</p>
                  </div>
                  <p className="text-sm">{formatPrice(i.price * i.quantity, order.currency)}</p>
                </div>
              ))}
            </div>
            <div className="flex justify-end mt-4 text-sm gap-6">
              <span style={{ color: "var(--ja-muted)" }}>Total</span>
              <span className="ja-serif text-lg">{formatPrice(order.total, order.currency)}</span>
            </div>
          </section>

          {/* Status update */}
          <section>
            <p className="ja-overline mb-4">Update Status & Tracking</p>
            <form onSubmit={saveStatus} className="grid grid-cols-2 gap-3">
              <label className="block col-span-2">
                <span className="ja-overline">Status</span>
                <select value={tracking.status} onChange={(e) => setTracking({ ...tracking, status: e.target.value })} className="ja-input mt-2" data-testid="drawer-status">
                  {STATUSES.map((s) => <option key={s}>{s}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="ja-overline">Courier</span>
                <input value={tracking.courier} onChange={(e) => setTracking({ ...tracking, courier: e.target.value })} placeholder="BlueDart / DHL …" className="ja-input mt-2" data-testid="drawer-courier" />
              </label>
              <label className="block">
                <span className="ja-overline">Tracking ID</span>
                <input value={tracking.tracking_id} onChange={(e) => setTracking({ ...tracking, tracking_id: e.target.value })} className="ja-input mt-2" data-testid="drawer-tracking-id" />
              </label>
              <label className="block col-span-2">
                <span className="ja-overline">Tracking URL</span>
                <input value={tracking.tracking_url} onChange={(e) => setTracking({ ...tracking, tracking_url: e.target.value })} placeholder="https://…" className="ja-input mt-2" data-testid="drawer-tracking-url" />
              </label>
              <div className="col-span-2 flex justify-end">
                <button className="ja-btn" data-testid="drawer-save-status">Save</button>
              </div>
            </form>
          </section>

          {/* Message buyer */}
          <section>
            <p className="ja-overline mb-4 flex items-center gap-2"><MessageSquare size={14} /> Message Buyer</p>
            <form onSubmit={sendMsg} className="space-y-3">
              <input value={msg.subject} onChange={(e) => setMsg({ ...msg, subject: e.target.value })} placeholder={`Subject (default: Update on ${order.order_number})`} className="ja-input" data-testid="drawer-msg-subject" />
              <textarea required rows={4} value={msg.body} onChange={(e) => setMsg({ ...msg, body: e.target.value })} placeholder="Message to buyer…" className="ja-input resize-none" data-testid="drawer-msg-body" />
              <div className="flex justify-end">
                <button className="ja-btn flex items-center gap-2" data-testid="drawer-send-msg"><Send size={14} /> Send</button>
              </div>
            </form>
            {messages.length > 0 && (
              <div className="mt-6 space-y-3 max-h-64 overflow-auto">
                <p className="ja-overline">Thread</p>
                {messages.map((m) => (
                  <div key={m.id} className="border p-3 text-sm" style={{ borderColor: "var(--ja-border)", background: m.from_role === "admin" ? "var(--ja-ivory)" : "white" }}>
                    <div className="flex justify-between text-xs tracking-[0.15em] uppercase" style={{ color: "var(--ja-muted)" }}>
                      <span>{m.from_role === "admin" ? "Atelier" : "Buyer"} · {m.from_name}</span>
                      <span>{new Date(m.created_at).toLocaleString()}</span>
                    </div>
                    {m.subject && <p className="ja-serif mt-2">{m.subject}</p>}
                    <p className="mt-1 whitespace-pre-wrap">{m.body}</p>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}

function Field({ k, v }) {
  return (
    <div>
      <p className="ja-overline" style={{ color: "var(--ja-muted)" }}>{k}</p>
      <p className="mt-2">{v || "—"}</p>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    "Order Placed": "var(--ja-muted)",
    "Packed": "var(--ja-gold)",
    "Shipped": "var(--ja-gold)",
    "Out For Delivery": "var(--ja-gold)",
    "Delivered": "var(--ja-success)",
    "Cancelled": "var(--ja-error)",
  };
  return <span className="text-[11px] tracking-[0.2em] uppercase px-3 py-1 border" style={{ borderColor: map[status] || "var(--ja-border)", color: map[status] }}>{status}</span>;
}

/* ---------- Customers ---------- */
function CustomersTab() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/admin/customers").then((r) => setItems(r.data)); }, []);
  return (
    <div>
      <SectionHead title="Customers" sub={`${items.length} registered patrons.`} />
      <div className="overflow-x-auto border" style={{ borderColor: "var(--ja-border)", background: "white" }}>
        <table className="w-full text-sm">
          <thead><tr className="border-b text-left text-[11px] tracking-[0.2em] uppercase" style={{ borderColor: "var(--ja-border)" }}>
            <th className="py-4 pl-6">Name</th><th>Email</th><th>Joined</th><th>Orders</th><th>Lifetime Value</th>
          </tr></thead>
          <tbody>
            {items.map((c) => (
              <tr key={c.id} className="border-b" style={{ borderColor: "var(--ja-border)" }} data-testid={`customer-${c.id}`}>
                <td className="py-4 pl-6 ja-serif">{c.name}</td>
                <td>{c.email}</td>
                <td>{new Date(c.created_at).toLocaleDateString()}</td>
                <td>{c.orders_count}</td>
                <td>{formatPrice(c.lifetime_value || 0)}</td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={5} className="py-10 text-center" style={{ color: "var(--ja-muted)" }}>No customers yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ---------- Coupons ---------- */
function CouponsTab() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ code: "", type: "percent", value: 10, min_subtotal: 0, active: true, usage_limit: 0 });
  const load = async () => { const { data } = await api.get("/coupons"); setItems(data); };
  useEffect(() => { load(); }, []);
  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/coupons", { ...form, value: parseFloat(form.value), min_subtotal: parseFloat(form.min_subtotal), usage_limit: parseInt(form.usage_limit) });
      toast.success("Coupon created"); setForm({ code: "", type: "percent", value: 10, min_subtotal: 0, active: true, usage_limit: 0 }); load();
    } catch (e2) { toast.error(formatApiError(e2.response?.data?.detail)); }
  };
  const del = async (code) => { await api.delete(`/coupons/${code}`); load(); };
  return (
    <div>
      <SectionHead title="Coupons" sub="Create discount codes for campaigns." />
      <form onSubmit={create} className="grid sm:grid-cols-6 gap-3 mb-8 p-6 border items-end" style={{ borderColor: "var(--ja-border)", background: "white" }}>
        <Input label="Code" value={form.code} onChange={(v) => setForm({ ...form, code: v })} required />
        <Select label="Type" value={form.type} onChange={(v) => setForm({ ...form, type: v })} opts={["percent", "flat"]} />
        <Input label="Value" type="number" value={form.value} onChange={(v) => setForm({ ...form, value: v })} required />
        <Input label="Min Subtotal" type="number" value={form.min_subtotal} onChange={(v) => setForm({ ...form, min_subtotal: v })} />
        <Input label="Usage Limit" type="number" value={form.usage_limit} onChange={(v) => setForm({ ...form, usage_limit: v })} />
        <button className="ja-btn" data-testid="admin-add-coupon">Add Coupon</button>
      </form>
      <table className="w-full text-sm border" style={{ borderColor: "var(--ja-border)", background: "white" }}>
        <thead><tr className="border-b text-left text-[11px] tracking-[0.2em] uppercase" style={{ borderColor: "var(--ja-border)" }}>
          <th className="py-4 pl-6">Code</th><th>Type</th><th>Value</th><th>Min</th><th>Used</th><th>Limit</th><th></th>
        </tr></thead>
        <tbody>
          {items.map((c) => (
            <tr key={c.code} className="border-b" style={{ borderColor: "var(--ja-border)" }} data-testid={`coupon-${c.code}`}>
              <td className="py-3 pl-6 ja-serif">{c.code}</td><td>{c.type}</td>
              <td>{c.type === "percent" ? `${c.value}%` : formatPrice(c.value)}</td>
              <td>{formatPrice(c.min_subtotal)}</td><td>{c.used_count || 0}</td><td>{c.usage_limit || "∞"}</td>
              <td><button onClick={() => del(c.code)} data-testid={`del-coupon-${c.code}`}><Trash2 size={14} /></button></td>
            </tr>
          ))}
          {items.length === 0 && <tr><td colSpan={7} className="py-10 text-center" style={{ color: "var(--ja-muted)" }}>No coupons yet.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

/* ---------- Auctions ---------- */
function AuctionsTab() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ title: "", description: "", image: "", starting_price: 100000, end_time: "", sku: "", metal: "", gemstone: "" });
  const load = async () => { const { data } = await api.get("/auctions"); setItems(data); };
  useEffect(() => { load(); }, []);
  const create = async (e) => {
    e.preventDefault();
    try {
      const end_iso = new Date(form.end_time).toISOString();
      await api.post("/auctions", { ...form, starting_price: parseFloat(form.starting_price), end_time: end_iso });
      toast.success("Auction created"); setForm({ title: "", description: "", image: "", starting_price: 100000, end_time: "", sku: "", metal: "", gemstone: "" }); load();
    } catch (e2) { toast.error(formatApiError(e2.response?.data?.detail)); }
  };
  const del = async (id) => { await api.delete(`/auctions/${id}`); load(); };
  return (
    <div>
      <SectionHead title="Auctions" sub="Curate one-of-one timed releases." />
      <form onSubmit={create} className="grid sm:grid-cols-2 gap-3 mb-8 p-6 border" style={{ borderColor: "var(--ja-border)", background: "white" }}>
        <Input label="Title" value={form.title} onChange={(v) => setForm({ ...form, title: v })} required />
        <Input label="SKU" value={form.sku} onChange={(v) => setForm({ ...form, sku: v })} required />
        <Input label="Image URL" value={form.image} onChange={(v) => setForm({ ...form, image: v })} required />
        <Input label="Starting Price (₹)" type="number" value={form.starting_price} onChange={(v) => setForm({ ...form, starting_price: v })} required />
        <label className="block">
          <span className="ja-overline">End Time</span>
          <input required type="datetime-local" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} className="ja-input mt-2" data-testid="auction-end" />
        </label>
        <Input label="Metal" value={form.metal} onChange={(v) => setForm({ ...form, metal: v })} />
        <textarea rows={3} placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="ja-input sm:col-span-2 resize-none" />
        <button className="ja-btn sm:col-span-2" data-testid="admin-add-auction">Create Auction</button>
      </form>
      <table className="w-full text-sm border" style={{ borderColor: "var(--ja-border)", background: "white" }}>
        <thead><tr className="border-b text-left text-[11px] tracking-[0.2em] uppercase" style={{ borderColor: "var(--ja-border)" }}>
          <th className="py-4 pl-6">Title</th><th>SKU</th><th>Current Bid</th><th>Ends</th><th>Bidder</th><th></th>
        </tr></thead>
        <tbody>
          {items.map((a) => (
            <tr key={a.id} className="border-b" style={{ borderColor: "var(--ja-border)" }} data-testid={`auction-${a.sku}`}>
              <td className="py-3 pl-6 ja-serif">{a.title}</td><td>{a.sku}</td>
              <td>{formatPrice(a.current_bid)}</td>
              <td>{new Date(a.end_time).toLocaleString()}</td>
              <td>{a.highest_bidder_name || "—"}</td>
              <td><button onClick={() => del(a.id)} data-testid={`del-auction-${a.sku}`}><Trash2 size={14} /></button></td>
            </tr>
          ))}
          {items.length === 0 && <tr><td colSpan={6} className="py-10 text-center" style={{ color: "var(--ja-muted)" }}>No auctions.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

/* ---------- Helpers ---------- */
function SectionHead({ title, sub, children }) {
  return (
    <div className="flex items-end justify-between mb-8 pb-6 border-b" style={{ borderColor: "var(--ja-border)" }}>
      <div>
        <h1 className="ja-serif text-4xl tracking-tight">{title}</h1>
        {sub && <p className="text-sm mt-2" style={{ color: "var(--ja-muted)" }}>{sub}</p>}
      </div>
      {children}
    </div>
  );
}

function ProductDialog({ form, setForm, editing, onClose, onSave }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="admin-edit-dialog">
      <form onSubmit={onSave} className="bg-white w-full max-w-3xl max-h-[90vh] overflow-auto p-8">
        <div className="flex justify-between items-center mb-6 border-b pb-4" style={{ borderColor: "var(--ja-border)" }}>
          <h3 className="ja-serif text-2xl">{editing === "new" ? "New Product" : "Edit Product"}</h3>
          <button type="button" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <Input label="SKU" value={form.sku} onChange={(v) => setForm({ ...form, sku: v })} required />
          <Input label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required />
          <Input label="Price" type="number" value={form.price} onChange={(v) => setForm({ ...form, price: v })} required />
          <Input label="Compare Price" type="number" value={form.compare_at_price || ""} onChange={(v) => setForm({ ...form, compare_at_price: v })} />
          <Input label="Stock" type="number" value={form.stock} onChange={(v) => setForm({ ...form, stock: v })} />
          <Select label="Category" value={form.category} onChange={(v) => setForm({ ...form, category: v })} opts={["Rings", "Necklaces", "Bracelets", "Earrings", "Bridal"]} />
          <Select label="Metal" value={form.metal || ""} onChange={(v) => setForm({ ...form, metal: v })} opts={["", "Silver", "Platinum", "9K Gold", "10K Gold", "14K Gold", "18K Gold"]} />
          <Select label="Color" value={form.color || ""} onChange={(v) => setForm({ ...form, color: v })} opts={["", "Yellow Gold", "White Gold", "Rose Gold"]} />
          <Select
  label="Gemstone"
  value={form.gemstone || ""}
  onChange={(v) => setForm({ ...form, gemstone: v })}
  opts={[
    "",
    "Agate",
    "Alexandrite",
    "Amazonite",
    "Amber",
    "Amethyst",
    "Ametrine",
    "Aquamarine",
    "Aventurine",

    "Beryl",
    "Bloodstone",
    "Blue Sapphire",

    "Carnelian",
    "Cat's Eye",
    "Chalcedony",
    "Chrysoberyl",
    "Chrysocolla",
    "Chrysoprase",
    "Citrine",
    "Coral",

    "Diamond",
    "Diopside",

    "Emerald",

    "Fire Opal",
    "Fluorite",

    "Garnet",
    "Green Tourmaline",

    "Iolite",
    "Jade",
    "Jasper",
    "Jet",

    "Kunzite",

    "Labradorite",
    "Lapis Lazuli",
    "Larimar",

    "Malachite",
    "Moonstone",
    "Morganite",
    "Moissanite",

    "Obsidian",
    "Onyx",
    "Opal",

    "Paraiba Tourmaline",
    "Pearl",
    "Peridot",
    "Pink Sapphire",
    "Pink Tourmaline",
    "Prehnite",

    "Quartz",
    "Rose Quartz",
    "Rhodolite Garnet",
    "Ruby",

    "Sapphire",
    "Smoky Quartz",
    "Spinel",
    "Star Ruby",
    "Star Sapphire",
    "Sunstone",

    "Tanzanite",
    "Tiger's Eye",
    "Topaz",
    "Tourmaline",
    "Tsavorite", 
    "Turquoise",

    "White Sapphire",

    "Yellow Sapphire",

    "Zircon",
    "Other"
  ]}
/>
          <Select label="Occasion" value={form.occasion || ""} onChange={(v) => setForm({ ...form, occasion: v })} opts={["", "Wedding", "Engagement", "Anniversary", "Birthday", "Vacation", "Office Wear"]} />
          <Input label="Purity" value={form.purity || ""} onChange={(v) => setForm({ ...form, purity: v })} />
          <Input label="Weight" value={form.weight || ""} onChange={(v) => setForm({ ...form, weight: v })} />
        </div>
        <div className="mt-4">
          <label className="ja-overline">Description</label>
          <textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full border p-3 text-sm mt-2" style={{ borderColor: "var(--ja-border)" }} />
        </div>
        <div className="mt-4">
          <label className="ja-overline">Image URLs</label>
          {form.images.map((url, i) => (
            <input key={i} value={url} onChange={(e) => { const a = [...form.images]; a[i] = e.target.value; setForm({ ...form, images: a }); }} placeholder={`Image URL ${i + 1}`} className="ja-input mt-2" />
          ))}
          <button type="button" onClick={() => setForm({ ...form, images: [...form.images, ""] })} className="text-xs tracking-[0.2em] uppercase mt-3">+ Add Image</button>
        </div>
        <div className="mt-6 grid grid-cols-2 md:grid-cols-5 gap-3 text-xs">
          {["is_new", "is_best_seller", "is_personalized", "is_lab_grown", "is_birthstone"].map((k) => (
            <label key={k} className="flex items-center gap-2 tracking-[0.15em] uppercase">
              <input type="checkbox" checked={!!form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.checked })} /> {k.replace("is_", "").replace("_", " ")}
            </label>
          ))}
        </div>
        <div className="flex justify-end gap-3 mt-8 pt-6 border-t" style={{ borderColor: "var(--ja-border)" }}>
          <button type="button" onClick={onClose} className="ja-btn-outline">Cancel</button>
          <button type="submit" className="ja-btn" data-testid="admin-save-product">Save</button>
        </div>
      </form>
    </div>
  );
}

function Input({ label, value, onChange, type = "text", required }) {
  return (
    <label className="block">
      <span className="ja-overline">{label}</span>
      <input required={required} type={type} value={value} onChange={(e) => onChange(e.target.value)} className="ja-input mt-2" />
    </label>
  );
}
function Select({ label, value, onChange, opts }) {
  return (
    <label className="block">
      <span className="ja-overline">{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)} className="ja-input mt-2">
        {opts.map((o) => <option key={o} value={o}>{o || "—"}</option>)}
      </select>
    </label>
  );
}
